(function () {
  if (customElements.get('cart-drawer')) return;

  var focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.itemsContainer = this.querySelector('[data-cart-drawer-items]');
      this.emptyState = this.querySelector('[data-cart-drawer-empty]');
      this.footer = this.querySelector('[data-cart-drawer-footer]');
      this.total = this.querySelector('[data-cart-drawer-total]');
      this.shipping = this.querySelector('[data-cart-drawer-shipping]');
      this.shippingText = this.querySelector('[data-cart-drawer-shipping-text]');
      this.shippingFill = this.querySelector('[data-cart-drawer-shipping-fill]');
      this.template = this.querySelector('[data-cart-drawer-item-template]');
      this.upsells = this.querySelector('[data-cart-upsells]');
      this.upsellGrid = this.querySelector('[data-cart-upsells-grid]');
      this.upsellLimit = Number(this.dataset.upsellLimit || 3);
      this.strings = this._readStrings();
      this.freeShippingEnabled = this.dataset.freeShippingEnabled === 'true';
      this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 0);
      this.closeTimer = null;
      this.lastFocusedElement = null;
      this.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SEK';
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.addEventListener('click', this._onClick.bind(this));
      document.addEventListener('click', this._onDocumentClick.bind(this));
      document.addEventListener('keydown', this._onKeydown.bind(this));
      document.addEventListener('cart:added', () => this.refresh(true));
      this._initUpsells();
    }

    open() {
      window.clearTimeout(this.closeTimer);
      this.lastFocusedElement = document.activeElement;
      this.hidden = false;
      this.classList.remove('is-closing');
      this.classList.remove('is-open');
      document.body.classList.add('cart-drawer-open');

      if (this.panel) this.panel.scrollTop = 0;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.classList.add('is-open');
          if (this.panel) this.panel.focus();
        });
      });
    }

    close() {
      if (this.hidden || this.classList.contains('is-closing')) return;

      window.clearTimeout(this.closeTimer);
      this.classList.remove('is-open');
      this.classList.add('is-closing');
      document.body.classList.remove('cart-drawer-open');
      this.closeTimer = window.setTimeout(() => {
        this.hidden = true;
        this.classList.remove('is-closing');
        if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
          this.lastFocusedElement.focus();
        }
      }, 340);
    }

    async refresh(openAfterRefresh) {
      try {
        var cart = await this._fetchCart();
        this._render(cart);
        if (openAfterRefresh) this.open();
      } catch (error) {
        if (openAfterRefresh) window.location.href = this.root + 'cart';
      }
    }

    async _changeItem(key, quantity) {
      this.classList.add('is-updating');
      try {
        var response = await fetch(this.root + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
        });

        if (!response.ok) throw new Error('Cart update failed');
        var cart = await response.json();
        this._render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      } finally {
        this.classList.remove('is-updating');
      }
    }

    async _fetchCart() {
      var response = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    }

    _render(cart) {
      this._renderItems(cart.items || []);
      this._renderSummary(cart);
      this._updateCartCount(cart.item_count || 0);
      this._updateUpsells(cart.items || []);
    }

    _renderItems(items) {
      if (!this.itemsContainer || !this.template) return;
      this.itemsContainer.innerHTML = '';

      items.forEach((item) => {
        var node = this.template.content.firstElementChild.cloneNode(true);
        var mediaLink = node.querySelector('.cart-drawer-item__media');
        var placeholder = node.querySelector('.cart-drawer-item__placeholder');
        var image = null;
        var title = node.querySelector('.cart-drawer-item__title');
        var price = node.querySelector('.cart-drawer-item__price');
        var variant = node.querySelector('.cart-drawer-item__variant');
        var quantity = node.querySelector('[data-cart-drawer-quantity-value]');

        node.dataset.lineKey = item.key;
        node.dataset.productId = item.product_id;
        if (mediaLink) mediaLink.href = item.url || '#';
        if (title) {
          title.href = item.url || '#';
          title.textContent = item.product_title || item.title || '';
        }
        if (price) price.textContent = this._formatMoney(item.final_line_price || item.line_price || 0);
        if (quantity) quantity.textContent = item.quantity;

        if (mediaLink && placeholder) {
          if (item.image) {
            image = document.createElement('img');
            image.className = 'cart-drawer-item__image';
            image.loading = 'lazy';
            image.width = 180;
            image.height = 240;
            image.src = this._imageUrl(item.image, 180);
            image.alt = item.product_title || item.title || '';
            mediaLink.insertBefore(image, placeholder);
            placeholder.hidden = true;
          } else {
            placeholder.hidden = false;
          }
        }

        var variantText = this._variantText(item);
        if (variant) {
          variant.textContent = variantText;
          variant.hidden = !variantText;
        }

        this.itemsContainer.appendChild(node);
      });
    }

    _initUpsells() {
      if (!this.upsells || !this.upsellGrid) return;

      var cards = Array.prototype.slice.call(this.upsellGrid.querySelectorAll('[data-cart-upsell-card]'));
      this._shuffle(cards).forEach((card) => this.upsellGrid.appendChild(card));
      this._updateUpsells(this._readCurrentItems());
    }

    _updateUpsells(items) {
      if (!this.upsells || !this.upsellGrid) return;

      var productIds = new Set((items || []).map((item) => String(item.product_id || item.productId || '')));
      var cards = Array.prototype.slice.call(this.upsellGrid.querySelectorAll('[data-cart-upsell-card]'));
      var visibleCount = 0;

      cards.forEach((card) => {
        var shouldShow = visibleCount < this.upsellLimit && !productIds.has(String(card.dataset.productId || ''));
        card.hidden = !shouldShow;
        if (shouldShow) visibleCount += 1;
      });

      this.upsells.hidden = visibleCount === 0;
    }

    _readCurrentItems() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-cart-drawer-item]')).map((item) => {
        return { productId: item.dataset.productId };
      });
    }

    _shuffle(items) {
      var shuffled = items.slice();
      for (var index = shuffled.length - 1; index > 0; index -= 1) {
        var randomIndex = Math.floor(Math.random() * (index + 1));
        var current = shuffled[index];
        shuffled[index] = shuffled[randomIndex];
        shuffled[randomIndex] = current;
      }
      return shuffled;
    }

    async _addUpsell(button) {
      var card = button.closest('[data-cart-upsell-card]');
      var variantId = card ? card.dataset.variantId : '';
      if (!variantId || button.classList.contains('is-loading')) return;

      var originalLabel = button.textContent;
      button.classList.add('is-loading');

      try {
        var response = await fetch(this.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });

        if (!response.ok) throw new Error('Upsell add failed');
        var cart = await this._fetchCart();
        this._render(cart);
        button.classList.add('is-added');
        button.textContent = this.strings.upsellAdded;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

        window.setTimeout(() => {
          button.classList.remove('is-added');
          button.textContent = originalLabel || this.strings.upsellAdd;
        }, 1600);
      } catch (error) {
        button.textContent = this.strings.upsellError;
        window.setTimeout(() => {
          button.textContent = originalLabel || this.strings.upsellAdd;
        }, 1800);
      } finally {
        button.classList.remove('is-loading');
      }
    }

    _renderSummary(cart) {
      var isEmpty = !cart.item_count;
      if (this.emptyState) this.emptyState.hidden = !isEmpty;
      if (this.itemsContainer) this.itemsContainer.hidden = isEmpty;
      if (this.footer) this.footer.hidden = isEmpty;
      if (this.total) this.total.textContent = this._formatMoney(cart.total_price || 0);
      this._renderShipping(cart.total_price || 0);
    }

    _renderShipping(totalPrice) {
      if (!this.freeShippingEnabled || !this.freeShippingThreshold || !this.shipping) return;

      var remaining = Math.max(this.freeShippingThreshold - totalPrice, 0);
      var progress = Math.min(Math.round((totalPrice / this.freeShippingThreshold) * 100), 100);

      if (this.shippingText) {
        if (remaining > 0) {
          this.shippingText.textContent = this.strings.freeShippingRemaining.replace('[amount]', this._formatMoney(remaining));
        } else {
          this.shippingText.textContent = this.strings.freeShippingReached;
        }
      }

      if (this.shippingFill) this.shippingFill.style.width = progress + '%';
    }

    _updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.hidden = count === 0;
      });
    }

    _onClick(event) {
      var close = event.target.closest('[data-cart-drawer-close]');
      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      var upsellButton = event.target.closest('[data-cart-upsell-add]');
      if (upsellButton) {
        event.preventDefault();
        this._addUpsell(upsellButton);
        return;
      }

      var quantityButton = event.target.closest('[data-cart-drawer-quantity]');
      if (quantityButton) {
        event.preventDefault();
        var item = quantityButton.closest('[data-cart-drawer-item]');
        var value = item ? item.querySelector('[data-cart-drawer-quantity-value]') : null;
        var key = item ? item.dataset.lineKey : '';
        var currentQuantity = value ? Number(value.textContent) : 0;
        var nextQuantity = quantityButton.dataset.cartDrawerQuantity === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
        if (key) this._changeItem(key, nextQuantity);
        return;
      }

      var removeButton = event.target.closest('[data-cart-drawer-remove]');
      if (removeButton) {
        event.preventDefault();
        var removeItem = removeButton.closest('[data-cart-drawer-item]');
        if (removeItem && removeItem.dataset.lineKey) this._changeItem(removeItem.dataset.lineKey, 0);
      }
    }

    _onDocumentClick(event) {
      var trigger = event.target.closest('[data-cart-drawer-open]');
      if (!trigger) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      this.refresh(true);
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && !this.hidden) this.close();
      if (event.key !== 'Tab' || this.hidden) return;

      var focusable = Array.prototype.slice.call(this.querySelectorAll(focusableSelector)).filter((element) => {
        return !element.hasAttribute('disabled') && element.offsetParent !== null;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _variantText(item) {
      if (!item.options_with_values || !item.options_with_values.length) return '';
      return item.options_with_values
        .filter((option) => option.value && option.value !== 'Default Title')
        .map((option) => option.name + ': ' + option.value)
        .join(' / ');
    }

    _formatMoney(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    }

    _imageUrl(url, width) {
      if (!url) return '';
      if (url.indexOf('?') > -1) return url + '&width=' + width;
      return url + '?width=' + width;
    }

    _readStrings() {
      var script = this.querySelector('[data-cart-drawer-strings]');
      if (!script) {
        return {
          freeShippingRemaining: 'Spend [amount] more for free shipping.',
          freeShippingReached: 'You have unlocked free shipping.',
          upsellAdd: 'Add',
          upsellAdded: 'Added',
          upsellError: 'Could not add',
        };
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return {
          freeShippingRemaining: 'Spend [amount] more for free shipping.',
          freeShippingReached: 'You have unlocked free shipping.',
          upsellAdd: 'Add',
          upsellAdded: 'Added',
          upsellError: 'Could not add',
        };
      }
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
